import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import "../Styling/viewAllLeave.css";

const LeaveBalancePage = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const year = new Date().getFullYear();

  const fetchLeaveBalance = async () => {
    if (!user?.organisationId) return;

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${Base_URL}/Leave/LeaveBalanceAll/${user?.organisationId}/${year}`
      );
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching leave balance:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveBalance();
  }, [user]);

  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;
    
    const query = searchQuery.toLowerCase();
    return employees.filter(emp => 
      emp.employee?.toLowerCase().includes(query) ||
      emp.resourceId?.toLowerCase().includes(query)
    );
  }, [employees, searchQuery]);

  const monthNames = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getBalanceColor = (balance) => {
    const num = parseFloat(balance);
    if (num < 0) return "#d32f2f"; // Red for negative
    if (num > 10) return "#2e7d32"; // Green for good balance
    if (num < 5) return "#ed6c02"; // Orange for low balance
    return "#1976d2"; // Blue for normal
  };

  return (
    <div className="lb-container">
      {/* Header Section */}
      <div className="lb-header">
        <div>
          <h1 className="lb-title">Leave Balance Report</h1>
          <p className="lb-subtitle">
            {year} • {user?.organisationName || "Organization"}
          </p>
        </div>
        
        <div className="lb-controls">
          {/* Search Input */}
          <div className="lb-search">
            <input
              type="text"
              placeholder="Search by employee name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="lb-search-input"
            />
            {searchQuery && (
              <button 
                className="lb-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {/* Month Selector */}
          <div className="lb-month-selector">
            <label className="lb-month-label">View Month:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="lb-month-dropdown"
            >
              {monthNames.map((m, index) =>
                index > 0 ? (
                  <option value={index} key={index}>
                    {m}
                  </option>
                ) : null
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      {filteredEmployees.length > 0 && (
        <div className="lb-stats">
          <div className="lb-stat-card">
            <span className="lb-stat-value">{filteredEmployees.length}</span>
            <span className="lb-stat-label">Total Employees</span>
          </div>
          <div className="lb-stat-card">
            <span className="lb-stat-value">
              {monthNames[selectedMonth]}
            </span>
            <span className="lb-stat-label">Selected Month</span>
          </div>
          <div className="lb-stat-card">
            <span className="lb-stat-value">{year}</span>
            <span className="lb-stat-label">Year</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="lb-loading">
          <div className="lb-spinner"></div>
          <p>Loading leave balance data...</p>
        </div>
      ) : (
        <>
          {/* Search Results Info */}
          {searchQuery && (
            <div className="lb-search-info">
              Found {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} 
              matching "{searchQuery}"
              <button 
                className="lb-clear-search-btn"
                onClick={() => setSearchQuery("")}
              >
                Clear search
              </button>
            </div>
          )}

          {/* No Results */}
          {filteredEmployees.length === 0 && employees.length > 0 && (
            <div className="lb-no-results">
              <p>No employees found matching "{searchQuery}"</p>
              <button 
                className="lb-clear-search-btn"
                onClick={() => setSearchQuery("")}
              >
                Show all employees
              </button>
            </div>
          )}

          {/* Employee List */}
          {filteredEmployees.length === 0 && employees.length === 0 && !isLoading && (
            <div className="lb-empty-state">
              <p>No leave balance data available</p>
            </div>
          )}

          <div className="lb-employee-list">
            {filteredEmployees?.map((emp, index) => (
              <div className="lb-employee-card" key={emp.resourceId || index}>
                {/* Employee Header */}
                <div className="lb-employee-header">
                  <div>
                    <h2 className="lb-employee-name">{emp.employee}</h2>
                    <div className="lb-employee-meta">
                      <span className="lb-employee-id">ID: {emp.resourceId}</span>
                      <span className="lb-employee-org">Organization: {emp.organisationId}</span>
                    </div>
                  </div>
                </div>

                {/* Yearly Summary */}
                <div className="lb-summary-section">
                  <h3 className="lb-section-title">
                    <span className="lb-section-year">{year}</span> Yearly Summary
                  </h3>
                  
                  <div className="lb-table-container">
                    <table className="lb-table">
                      <thead>
                        <tr>
                          <th>Leave Type</th>
                          <th>Credit</th>
                          <th>Debit</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emp?.yearly?.balance?.map((item, i) => (
                          <tr key={i}>
                            <td className="lb-leave-type">
                              <span className="lb-leave-badge">{item.name}</span>
                            </td>
                            <td className="lb-credit">{item.credit}</td>
                            <td className="lb-debit">{item.debit}</td>
                            <td 
                              className="lb-balance"
                              style={{ color: getBalanceColor(item.balance) }}
                            >
                              <strong>{item.balance}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Monthly Summary */}
                <div className="lb-summary-section">
                  <h3 className="lb-section-title">
                    <span className="lb-section-month">{monthNames[selectedMonth]}</span> Monthly Summary
                  </h3>
                  
                  <div className="lb-table-container">
                    <table className="lb-table">
                      <thead>
                        <tr>
                          <th>Leave Type</th>
                          <th>Credit</th>
                          <th>Debit</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emp?.monthly?.[selectedMonth]?.balance?.length > 0 ? (
                          emp.monthly[selectedMonth].balance.map((item, i) => (
                            <tr key={i}>
                              <td className="lb-leave-type">
                                <span className="lb-leave-badge">{item.name}</span>
                              </td>
                              <td className="lb-credit">{item.credit}</td>
                              <td className="lb-debit">{item.debit}</td>
                              <td 
                                className="lb-balance"
                                style={{ color: getBalanceColor(item.balance) }}
                              >
                                <strong>{item.balance}</strong>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="lb-no-data">
                            <td colSpan="4">
                              <div className="lb-no-data-content">
                                No leave activity for {monthNames[selectedMonth]}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LeaveBalancePage;